<template>
    <div>
        <div v-if="loadingLien" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary-dark"></v-progress-circular>
        </div>
        <v-alert v-else-if="loadError" type="error" variant="tonal">{{ loadError }}</v-alert>
        <v-form v-else ref="formEditLienGroupeInscription" class="spo-card rounded bordered p-2">
            <div class="text-body-2 mb-4">
                <strong>{{ lien_groupe.nom }}</strong>
                — {{ nb_inscrits }} inscription<span v-if="nb_inscrits > 1">s</span>
                (sur {{ nb_commandes }} commande<span v-if="nb_commandes > 1">s</span>)
            </div>
            <div class="d-flex align-center sm-gap mb-2">
                <v-icon icon="mdi-account-group" color="primary-dark"></v-icon>
                <div class="text-h6 font-weight-bold text-decoration-underline">Informations du groupe</div>
            </div>
            <v-row>
                <v-col md="4" cols="12">
                    <label class="field_label">Nom du groupe <span class="text-red">*</span></label>
                    <v-text-field v-model="lien_groupe.nom" required
                        :rules="[$formRules.required, $formRules.nom_groupe]"></v-text-field>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Nom du responsable du groupe</label>
                    <v-text-field v-model="lien_groupe.nom_responsable"></v-text-field>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Mail du responsable du groupe</label>
                    <v-text-field v-model="lien_groupe.mail_responsable"></v-text-field>
                </v-col>
                <v-col cols="12">
                    <label class="field_label">Informations supplémentaires</label>
                    <v-textarea v-model="lien_groupe.informations_supplementaires" rows="4" auto-grow
                        hide-details></v-textarea>
                </v-col>
            </v-row>
            <div class="d-flex align-center sm-gap mb-2 mt-4">
                <v-icon icon="mdi-lock-alert" color="primary-dark"></v-icon>
                <div class="text-h6 font-weight-bold text-decoration-underline">Limites du lien</div>
            </div>
            <v-row>
                <v-col md="4" cols="12">
                    <label class="field_label">Nombre de participants qui peuvent être inscrit avec ce code<span
                            class="text-red">*</span></label>
                    <div class="text-caption">Laisser à 0 pour aucune limite d'utilisation. Une limite supérieure à 0 ne
                        peut pas être inférieure au nombre d'inscrits ({{ nb_inscrits }}).</div>
                    <v-text-field v-model="lien_groupe.max_inscrits" required type="number" min="0"
                        :rules="[maxInscritsRule]"></v-text-field>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Date d'expiration <span class="text-red">*</span></label>
                    <v-text-field v-model="lien_groupe.date_expiration" type="date"
                        :rules="[dateExpirationRequiredRule]"></v-text-field>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Autoriser l'inscription avant l'ouverture du module ?<span
                            class="text-red">*</span></label>
                    <div class="text-caption">Si non, le lien ne sera utilisable qu'à partir de la date d'ouverture des
                        inscriptions</div>
                    <v-select :items="items" v-model="lien_groupe.allow_before_opening" hide-details item-title="label"
                        item-value="value"></v-select>
                </v-col>
            </v-row>
            <div class="d-flex align-center sm-gap mb-2 mt-4">
                <v-icon icon="mdi-routes" color="primary-dark"></v-icon>
                <div class="text-h6 font-weight-bold text-decoration-underline">Configuration des parcours</div>
            </div>
            <v-alert v-if="paiementLocked" type="info" variant="tonal" density="compact" class="mb-3">
                Une inscription ou une commande a déjà été faite via ce lien. Le paiement des parcours et des options ne
                peut plus être modifié.
            </v-alert>
            <v-row>
                <v-col md="4" cols="12">
                    <label class="field_label">Les parcours seront payant lors de l'inscription ?<span
                            class="text-red">*</span></label>
                    <div class="text-caption">Si non, seront payés par le responsable a posteriori pour tout le groupe
                    </div>
                    <v-select :items="items" v-model="lien_groupe.doit_payer" hide-details item-title="label"
                        item-value="value" :disabled="paiementLocked"></v-select>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Peut s'inscrire à des parcours complets ?<span
                            class="text-red">*</span></label>
                    <div class="text-caption">Si oui, les jauges peuvent être dépassées
                    </div>
                    <v-select :items="items" v-model="lien_groupe.inscription_course_complete" hide-details
                        item-title="label" item-value="value"></v-select>
                </v-col>
            </v-row>
            <v-row v-if="availables_parcours.length > 0">
                <v-col cols="12">
                    <label class="field_label">Restreindre les parcours disponibles ?<span
                            class="text-red">*</span></label>
                    <div v-if="lien_groupe.limit_acces_parcours" class="text-caption mb-2">
                        Le lien sera limité aux parcours : <strong>{{ list_filtered_parcours }}</strong>
                    </div>
                    <div v-else class="text-caption mb-2">
                        Aucun parcours coché: Le lien sera valide pour tous les parcours
                    </div>
                    <div class="d-inline-flex justify-start flex-wrap">
                        <v-checkbox class="mr-4" v-for="course in availables_parcours" v-model="course.selected"
                            hide-details density="compact" :label="course.nom" :key="course.id"></v-checkbox>
                    </div>
                </v-col>
            </v-row>
            <div class="d-flex align-center sm-gap mb-2 mt-4">
                <v-icon icon="mdi-tag-plus" color="primary-dark"></v-icon>
                <div class="text-h6 font-weight-bold text-decoration-underline">Configuration des
                    options/activités/boutique</div>
            </div>
            <v-row>
                <v-col md="4" cols="12">
                    <label class="field_label">Les options/activités/boutique seront payant lors de l'inscription
                        ?<span class="text-red">*</span></label>
                    <div class="text-caption">Si non, seront payés par le responsable a posteriori pour tout le groupe
                    </div>
                    <v-select :items="items" v-model="lien_groupe.doit_payer_options" hide-details item-title="label"
                        item-value="value" :disabled="paiementLocked"></v-select>
                </v-col>
                <v-col md="4" cols="12">
                    <label class="field_label">Peut s'inscrire à des options/activités/boutique complets ?<span
                            class="text-red">*</span></label>
                    <div class="text-caption">Si oui, les jauges peuvent être dépassées
                    </div>
                    <v-select :items="items" v-model="lien_groupe.inscriptions_options_completes" hide-details
                        item-title="label" item-value="value"></v-select>
                </v-col>
            </v-row>
            <div class="d-flex justify-center ga-2 mt-4 flex-wrap">

                <SportipsButton @onClick="validate()" icon="mdi-content-save" action-button :loading="saving" chevron>
                    Enregistrer les modifications
                </SportipsButton>
            </div>
        </v-form>
    </div>
</template>

<script>
import AdminService from "@/services/admin.service";
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
export default {
    data() {
        return {
            loadingLien: true,
            loadError: "",
            saving: false,
            nb_inscrits: 0,
            nb_commandes: 0,
            lien_groupe: {
                id: null,
                token: "",
                nom: "",
                nom_responsable: "",
                mail_responsable: "",
                informations_supplementaires: "",
                max_inscrits: 0,
                doit_payer: true,
                inscription_course_complete: false,
                date_expiration: "",
                limit_acces_parcours: false,
                filter_parcours: "",
                doit_payer_options: true,
                inscriptions_options_completes: false,
                allow_before_opening: false,
            },
            items: [
                { label: 'Oui', value: true },
                { label: 'Non', value: false },
            ],
            availables_parcours: [],
        };
    },
    watch: {
        availables_parcours: {
            handler(val) {
                if (!Array.isArray(val) || val.length === 0) return;
                const selected = val.filter(c => c.selected);
                this.lien_groupe.limit_acces_parcours = selected.length > 0;
                this.lien_groupe.filter_parcours = selected.map(c => c.id).join(',');
            },
            deep: true,
        },
    },
    computed: {
        ...mapStores(useMainStore),
        paiementLocked() {
            return this.nb_inscrits >= 1 || this.nb_commandes >= 1;
        },
        list_filtered_parcours() {
            return this.availables_parcours
                .filter(c => c.selected)
                .map(c => c.nom)
                .join(", ");
        },
    },
    methods: {
        toBool(value) {
            return value === true || value === 1 || value === '1';
        },
        dateExpirationRequiredRule(value) {
            if (!value) return "La date d'expiration est obligatoire";
            return true;
        },
        maxInscritsRule(value) {
            const n = parseInt(value, 10);
            if (Number.isNaN(n) || n < 0) {
                return "Veuillez saisir un entier positif ou zéro.";
            }
            if (n > 0 && n < this.nb_inscrits) {
                return `La limite doit être au moins égale au nombre d'inscrits actuels (${this.nb_inscrits}). La valeur 0 supprime la limite.`;
            }
            return true;
        },
        build_available_parcours(courses) {
            const ids = String(this.lien_groupe.filter_parcours || '')
                .split(',')
                .map(id => id.trim())
                .filter(id => id);
            this.availables_parcours = courses.map(course => ({
                id: course.id,
                nom: course.nom,
                selected: ids.includes(String(course.id)),
            }));
        },
        applyLien(lien) {
            this.nb_inscrits = parseInt(lien.nb_inscrits, 10) || 0;
            this.nb_commandes = parseInt(lien.nb_commandes, 10) || 0;
            const date = lien.date_expiration ? String(lien.date_expiration).slice(0, 10) : "";
            this.lien_groupe = {
                id: lien.id,
                token: lien.token,
                nom: lien.nom || "",
                nom_responsable: lien.nom_responsable || "",
                mail_responsable: lien.mail_responsable || "",
                informations_supplementaires: lien.informations_supplementaires || "",
                max_inscrits: parseInt(lien.max_inscrits, 10) || 0,
                doit_payer: this.toBool(lien.doit_payer),
                inscription_course_complete: this.toBool(lien.inscription_course_complete),
                date_expiration: date === "0000-00-00" ? "" : date,
                limit_acces_parcours: this.toBool(lien.limit_acces_parcours),
                filter_parcours: lien.filter_parcours || "",
                doit_payer_options: this.toBool(lien.doit_payer_options),
                inscriptions_options_completes: this.toBool(lien.inscriptions_options_completes),
                allow_before_opening: this.toBool(lien.allow_before_opening),
            };
        },
        goBack() {
            this.$router.push({
                name: 'LienGroupeInscription',
                params: { base_module: this.$route.params.base_module },
            });
        },
        async validate() {
            this.saving = true;
            const res = await this.$refs.formEditLienGroupeInscription.validate();
            if (res.valid) {
                const payload = {
                    ...this.lien_groupe,
                    id_module: this.mainStore.module.id,
                    max_inscrits: parseInt(this.lien_groupe.max_inscrits, 10),
                };
                const request = await AdminService.updateLienGroupe(payload);
                if (request && !request.error && request.id) {
                    this.applyLien(request);
                    this.mainStore.notification.send("Groupe mis à jour.", "success");
                } else {
                    this.mainStore.notification.send(
                        typeof request?.error === 'string' ? request.error : "Impossible d'enregistrer les modifications.",
                        "error"
                    );
                }
            }
            this.saving = false;
        },
    },
    async created() {
        const token = this.$route.params.token;
        const lien = await AdminService.getLienGroupe(token, this.mainStore.module.id);
        if (!lien || lien.error || !lien.id) {
            this.loadError = typeof lien?.error === 'string' ? lien.error : "Lien de groupe introuvable.";
            this.loadingLien = false;
            return;
        }
        this.applyLien(lien);
        let courses = this.mainStore.module.courses.list;
        if (!courses || courses.length === 0) {
            courses = await AdminService.getCourses(this.mainStore.module.id) || [];
        }
        this.build_available_parcours(courses);
        this.loadingLien = false;
    },
}
</script>

<style lang="scss" scoped></style>
